import React, { useState } from "react";
import { api, formatUSD } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import DirectDepositForm from "./DirectDepositForm";

export default function LoadFundsButton({ card, onDone, size = "default", hideTrigger = false, defaultOpen = false, prefillBank = null, prefillAmount = null }) {
  // When prefillBank is provided, skip the method picker and open the bank form directly.
  const [open, setOpen] = useState(defaultOpen && !prefillBank);
  const [method, setMethod] = useState(prefillBank ? "bank_account" : "card_or_crypto");
  const [formOpen, setFormOpen] = useState(!!(prefillBank && defaultOpen));
  const [servicePct, setServicePct] = useState(null);
  const [amount, setAmount] = useState(Number(prefillAmount) > 0 ? Number(prefillAmount) : 50);
  const [busy, setBusy] = useState(false);
  const { isImpersonating } = useAuth();

  React.useEffect(() => {
    api.get("/public/rates")
      .then((r) => setServicePct(Number(r.data?.service_fee_percent ?? 0)))
      .catch(() => setServicePct(0));
  }, []);

  const pctLabel = servicePct != null ? `${servicePct}%` : "service";
  const fee = amount > 0 && servicePct != null ? Math.round(amount * servicePct) / 100 : 0;
  const total = amount > 0 ? Math.round((amount + fee) * 100) / 100 : 0;

  // For card_or_crypto we skip the second dialog entirely — the user picked
  // the method and entered the amount in this dialog, so we go straight to
  // Stripe Checkout. For bank_account we still need holder/routing/account
  // fields, so we open the secondary form.
  const choose = async (m) => {
    if (m === "card_or_crypto") {
      if (!(amount > 0)) {
        toast.error("Enter an amount greater than 0");
        return;
      }
      setBusy(true);
      try {
        const r = await api.post(`/cards/${card.id}/load-funds-checkout`, {
          amount: Number(amount),
          payment_method: "card_or_crypto",
          origin_url: window.location.origin,
        });
        if (r.data?.checkout_url) {
          window.location.href = r.data.checkout_url;
          return;
        }
        toast.error("Stripe session could not be created");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not start checkout");
      } finally {
        setBusy(false);
      }
      return;
    }
    setMethod(m);
    setOpen(false);
    setFormOpen(true);
  };

  const triggerClass = size === "sm"
    ? "h-9 rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-white px-3 text-xs"
    : "rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white";

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => {
        setOpen(v);
        if (!v && hideTrigger && !formOpen && onDone) onDone();
      }}>
        {!hideTrigger && (
          <Button
            type="button"
            className={`${triggerClass} disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
            disabled={isImpersonating}
            title={isImpersonating ? "View-as mode is read-only" : undefined}
            data-testid="load-funds-open-btn"
          >
            <Plus size={size === "sm" ? 13 : 16} className="mr-1" /> Load funds
          </Button>
        )}
        <DialogContent className="max-w-md bg-[#FBF9F6] border-[#E5E0D8]" data-testid="load-funds-method-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-3xl">Load funds</DialogTitle>
            <DialogDescription className="text-[#4A5C50]">Funds are credited to the card instantly. A {pctLabel} service fee is deposited into the merchant bank account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-2 rounded-xl border-[#E5E0D8]" data-testid="load-funds-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="card_or_crypto" data-testid="method-option-card-crypto">Debit card (Visa / MC / Amex)</SelectItem>
                  <SelectItem value="bank_account" data-testid="method-option-bank">Bank account (ACH — no processing fee)</SelectItem>
                  <SelectItem value="direct_deposit" data-testid="method-option-direct-deposit">Direct deposit / wire to receiver bank (off-platform)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#4A5C50] mt-2 leading-relaxed">
                {method === "card_or_crypto"
                  ? "Redirects to Stripe Checkout. Supports any debit card globally. Funds credit the virtual card within seconds of payment confirmation."
                  : method === "bank_account"
                  ? "Uses your previously verified bank account via micro-deposits. Cheapest option but requires a one-time ACH setup."
                  : "Skip the card entirely — wire directly to the receiver's bank from your own bank. Zcardon logs the transfer details and generates a printable proof-of-payment receipt for your records."}
              </p>
            </div>

            {method === "direct_deposit" && (
              <div className="pt-2" data-testid="load-funds-direct-deposit-inline">
                <DirectDepositForm
                  cardId={card?.id || null}
                  compact
                  onSuccess={() => { setOpen(false); onDone && onDone(); }}
                />
              </div>
            )}

            {method === "card_or_crypto" && (
              <div data-testid="load-funds-inline-amount">
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Amount to load (USD)</Label>
                <input
                  type="number" min="1" step="0.01"
                  className="underline-input mono text-2xl"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="inline-load-amount"
                  required
                />
                <div className="flex flex-wrap gap-2 mt-2" data-testid="quick-amount-chips">
                  {[25, 50, 100, 250, 500].map((v) => {
                    const active = Number(amount) === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setAmount(v)}
                        className={`px-3 py-1 rounded-full text-xs mono border transition-all ${
                          active
                            ? "bg-[#184A30] text-white border-[#184A30]"
                            : "bg-white text-[#184A30] border-[#184A30]/30 hover:border-[#184A30]"
                        }`}
                        data-testid={`quick-amount-${v}`}
                      >
                        ${v}
                      </button>
                    );
                  })}
                </div>
                <div className="bg-[#112318] text-[#FBF9F6] rounded-2xl p-4 text-sm mt-3">
                  <div className="flex justify-between"><span className="opacity-70">Credited to card</span><span className="mono">{formatUSD(amount || 0)}</span></div>
                  <div className="flex justify-between"><span className="opacity-70">Service fee ({servicePct != null ? `${servicePct}%` : "…"})</span><span className="mono">{formatUSD(fee)}</span></div>
                  <div className="h-px bg-white/10 my-2" />
                  <div className="flex justify-between text-[#D96C4A] font-semibold"><span>Total charged</span><span className="mono">{formatUSD(total)}</span></div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="load-funds-method-cancel">Cancel</Button>
            {method !== "direct_deposit" && (
              <Button
                onClick={() => choose(method)}
                disabled={busy || (method === "card_or_crypto" && !(amount > 0))}
                className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white disabled:opacity-50"
                data-testid="load-funds-method-continue"
              >
                {method === "card_or_crypto"
                  ? (busy ? "Redirecting to Stripe…" : `Continue to Stripe · ${formatUSD(total)}`)
                  : "Continue"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={formOpen} onOpenChange={(v) => {
        setFormOpen(v);
        if (!v && onDone) onDone();
      }}>
        {formOpen && (
          <LoadFundsForm
            card={card}
            method={method}
            prefillBank={prefillBank}
            prefillAmount={prefillAmount}
            onClose={() => { setFormOpen(false); onDone && onDone(); }}
          />
        )}
      </Dialog>
    </>
  );
}

function LoadFundsForm({ card, method, onClose, prefillBank, prefillAmount }) {
  const [busy, setBusy] = useState(false);
  const [holdingBank, setHoldingBank] = useState(null);
  const [servicePct, setServicePct] = useState(null); // from /public/rates (live, admin-configurable)
  const [savedBanks, setSavedBanks] = useState([]);
  const [pickedBankId, setPickedBankId] = useState(prefillBank?.id || "");
  const [form, setForm] = useState({
    amount: Number(prefillAmount) > 0 ? Number(prefillAmount) : 50,
    holder_name: prefillBank?.holder_name || "",
    card_number: "",
    exp: "",
    cvv: "",
    routing_number: prefillBank?.routing_number || "",
    account_number: prefillBank?.account_number || "",
    bankVerified: !!prefillBank,
  });

  React.useEffect(() => {
    api.get("/public/holding-bank").then((r) => setHoldingBank(r.data)).catch(() => {});
    api.get("/public/rates")
      .then((r) => setServicePct(Number(r.data?.service_fee_percent ?? 0)))
      .catch(() => setServicePct(0));
    if (method === "bank_account" && !prefillBank) {
      api.get("/bank-verification/mine")
        .then((r) => setSavedBanks((r.data || []).filter((b) => b.status === "verified")))
        .catch(() => {});
    }
  }, [method, prefillBank]);

  const applySavedBank = async (bankId) => {
    if (!bankId) {
      // "Use a new bank" → clear the form back to empty
      setPickedBankId("");
      setForm((f) => ({ ...f, holder_name: "", routing_number: "", account_number: "", bankVerified: false }));
      return;
    }
    try {
      const r = await api.post(`/bank-verification/${bankId}/use`);
      const b = r.data;
      setPickedBankId(bankId);
      setForm((f) => ({
        ...f,
        holder_name: b.holder_name || "",
        routing_number: b.routing_number || "",
        account_number: b.account_number || "",
        bankVerified: true,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load saved bank");
    }
  };

  const amt = Number(form.amount);
  // Use the live admin-configurable rate so the displayed total matches exactly what backend charges.
  const fee = amt > 0 && servicePct != null ? Math.round(amt * servicePct) / 100 : 0;
  const total = amt > 0 ? Math.round((amt + fee) * 100) / 100 : 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // New path: card_or_crypto → redirect to Stripe Checkout for REAL money charge
      if (method === "card_or_crypto") {
        const r = await api.post(`/cards/${card.id}/load-funds-checkout`, {
          amount: Number(form.amount),
          payment_method: "card_or_crypto",
          origin_url: window.location.origin,
        });
        if (r.data.checkout_url) {
          window.location.href = r.data.checkout_url;
          return;
        }
        toast.error("Stripe session could not be created");
        return;
      }

      // Legacy path: bank_account (ACH via pre-verified micro-deposit)
      const payload = {
        method,
        amount: Number(form.amount),
        holder_name: form.holder_name,
        routing_number: method === "bank_account" ? form.routing_number : undefined,
        account_number: method === "bank_account" ? form.account_number : undefined,
      };
      const r = await api.post(`/cards/${card.id}/load-funds`, payload);
      toast.success(`Credited ${formatUSD(r.data.credited_to_card)} to the card`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load funds");
    } finally { setBusy(false); }
  };

  const title = method === "card_or_crypto" ? "Pay by Card or Crypto" : "Bank account details";

  return (
    <DialogContent className="max-w-xl bg-[#FBF9F6] border-[#E5E0D8]" data-testid="load-funds-form-dialog">
      <DialogHeader>
        <DialogTitle className="serif text-3xl">{title}</DialogTitle>
        <DialogDescription className="text-[#4A5C50]">
          {method === "card_or_crypto"
            ? "Redirects to Stripe Checkout. Card details stay on Stripe's PCI-compliant page — we never see them. Funds credit the card within seconds of payment confirmation."
            : "Uses your pre-verified bank account via ACH. Credits instantly in the sandbox."}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="space-y-5 py-2">
        <div>
          <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Amount to load (USD)</Label>
          <input
            type="number" min="1" step="0.01"
            className="underline-input mono text-2xl"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            data-testid="load-amount"
            required
          />
        </div>

        {method === "card_or_crypto" ? (
          <div className="bg-white border border-[#E5E0D8] rounded-xl p-4 text-sm space-y-2" data-testid="card-crypto-preview">
            <div className="flex items-center gap-3 pb-2 border-b border-[#E5E0D8]">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Accepted</span>
              <span className="ml-auto flex items-center gap-2 mono text-[10px] uppercase tracking-widest">
                <span className="px-2 py-0.5 bg-[#184A30] text-white rounded-full">Visa / MC / Amex</span>
              </span>
            </div>
            <p className="text-xs text-[#4A5C50] leading-relaxed">
              You'll be redirected to Stripe's hosted Checkout page. Supported: credit/debit cards, Apple Pay, Google Pay. No 3-D Secure surprises — Stripe handles all fraud checks.
            </p>
          </div>
        ) : (
          <>
            {savedBanks.length > 0 && (
              <div
                className="rounded-xl border border-[#184A30]/20 bg-[#184A30]/5 p-3 mb-3"
                data-testid="saved-banks-picker"
              >
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-2">Use a saved bank</p>
                <div className="flex flex-wrap gap-2">
                  {savedBanks.map((b) => {
                    const tail = (b.account_number || "").slice(-4);
                    const isPicked = pickedBankId === b.id;
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => applySavedBank(b.id)}
                        className={`px-3 py-1.5 rounded-full text-xs mono border transition-all ${isPicked ? "bg-[#184A30] text-white border-[#184A30]" : "bg-white text-[#184A30] border-[#184A30]/30 hover:border-[#184A30]"}`}
                        data-testid={`saved-bank-${b.id}`}
                      >
                        {b.holder_name?.split(" ")[0] || "Bank"} · ••••{tail}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => applySavedBank("")}
                    className={`px-3 py-1.5 rounded-full text-xs mono border transition-all ${!pickedBankId ? "bg-[#184A30] text-white border-[#184A30]" : "bg-white text-[#4A5C50] border-[#E5E0D8] hover:border-[#4A5C50]"}`}
                    data-testid="saved-bank-new"
                  >
                    + New bank
                  </button>
                </div>
                {pickedBankId && (
                  <p className="text-[11px] text-[#184A30] mt-2 flex items-center gap-1">
                    <span>✓</span> Verified · skip verification, just confirm and load
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Cardholder / account name</Label>
              <input
                className="underline-input"
                value={form.holder_name}
                onChange={(e) => { setPickedBankId(""); setForm({ ...form, holder_name: e.target.value }); }}
                data-testid="load-holder-name"
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Routing number (ABA)</Label>
              <input
                inputMode="numeric"
                className="underline-input mono"
                value={form.routing_number}
                onChange={(e) => { setPickedBankId(""); setForm({ ...form, routing_number: e.target.value, bankVerified: false }); }}
                placeholder="021000021"
                maxLength={9}
                data-testid="load-routing"
                required
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">Account number</Label>
              <input
                inputMode="numeric"
                className="underline-input mono"
                value={form.account_number}
                onChange={(e) => { setPickedBankId(""); setForm({ ...form, account_number: e.target.value, bankVerified: false }); }}
                placeholder="000123456789"
                data-testid="load-account"
                required
              />
            </div>
            <BankVerifyBlock
              holder={form.holder_name}
              routing={form.routing_number}
              account={form.account_number}
              verified={!!form.bankVerified}
              onVerified={() => setForm((f) => ({ ...f, bankVerified: true }))}
            />
          </>
        )}

        <div className="bg-[#112318] text-[#FBF9F6] rounded-2xl p-5 text-sm">
          <div className="flex justify-between"><span className="opacity-70">Credited to card</span><span className="mono">{formatUSD(amt)}</span></div>
          <div className="flex justify-between"><span className="opacity-70">Service fee ({servicePct != null ? `${servicePct}%` : "…"}) → merchant bank</span><span className="mono">{formatUSD(fee)}</span></div>
          <div className="h-px bg-white/10 my-2" />
          <div className="flex justify-between text-[#D96C4A] font-semibold"><span>Total charged</span><span className="mono">{formatUSD(total)}</span></div>
          {holdingBank && holdingBank.bank_name && (
            <div className="mt-4 pt-4 border-t border-white/10 text-xs opacity-80" data-testid="load-funds-holding-bank">
              <p className="text-[10px] tracking-[0.3em] uppercase opacity-60 mb-1">Funds will be held at</p>
              <p className="mono">{holdingBank.bank_name}{holdingBank.account_masked ? ` · ${holdingBank.account_masked}` : ""}</p>
              {holdingBank.memo_note && <p className="opacity-70 mt-1">{holdingBank.memo_note}</p>}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} data-testid="load-funds-form-cancel">Cancel</Button>
          <Button
            type="submit"
            disabled={busy || (method === "bank_account" && !form.bankVerified)}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white disabled:opacity-50"
            title={method === "bank_account" && !form.bankVerified ? "Verify your bank account with micro-deposits first" : undefined}
            data-testid="load-funds-submit"
          >
            {busy
              ? (method === "card_or_crypto" ? "Redirecting to Stripe…" : "Loading…")
              : (method === "card_or_crypto" ? `Continue to Stripe · ${formatUSD(total)}` : `Load ${formatUSD(amt)}`)}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function BankVerifyBlock({ holder, routing, account, verified, onVerified }) {
  const [state, setState] = useState("idle"); // idle | deposited | verified
  const [id, setId] = useState(null);
  const [amt1, setAmt1] = useState("");
  const [amt2, setAmt2] = useState("");
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false);

  const canStart = holder && routing.length === 9 && account.length >= 4;

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bank-verification/start", {
        holder_name: holder,
        routing_number: routing,
        account_number: account,
      });
      if (r.data.already_verified) {
        onVerified();
        setState("verified");
        toast.success("This bank is already verified on your account");
        return;
      }
      setId(r.data.id);
      setHint(r.data.sandbox_amounts || null);
      setState("deposited");
      toast.info("Two micro-deposits queued. Confirm the amounts below to verify.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start verification");
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/bank-verification/confirm", {
        id,
        amount_1_cents: Number(amt1),
        amount_2_cents: Number(amt2),
      });
      setState("verified");
      onVerified();
      toast.success("Bank account verified");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wrong amounts");
    } finally { setBusy(false); }
  };

  if (verified || state === "verified") {
    return (
      <div className="rounded-xl bg-[#184A30]/10 text-[#184A30] px-4 py-3 text-sm flex items-center gap-2" data-testid="bank-verify-ok">
        <span className="inline-block w-2 h-2 rounded-full bg-[#184A30]" /> Bank verified
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-4 space-y-3" data-testid="bank-verify-panel">
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#8C6B21]">ACH verification required</p>
      {state === "idle" && (
        <>
          <p className="text-sm text-[#4A5C50]">We'll send two small deposits (under $1 each). Confirm the amounts to prove you own this bank account.</p>
          <Button
            type="button"
            onClick={start}
            disabled={!canStart || busy}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
            data-testid="bank-verify-start-btn"
          >
            {busy ? "Sending…" : "Send micro-deposits"}
          </Button>
        </>
      )}
      {state === "deposited" && (
        <>
          <p className="text-sm text-[#4A5C50]">Two deposits sent to {account && `•••• ${account.slice(-4)}`}. Enter their amounts in cents (e.g., 42 = $0.42).</p>
          {hint && (
            <p className="text-[10px] mono text-[#8C6B21]">Sandbox hint: {hint[0]}¢ and {hint[1]}¢</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <input inputMode="numeric" placeholder="First ¢" className="underline-input mono" value={amt1} onChange={(e) => setAmt1(e.target.value)} data-testid="bank-verify-a1" />
            <input inputMode="numeric" placeholder="Second ¢" className="underline-input mono" value={amt2} onChange={(e) => setAmt2(e.target.value)} data-testid="bank-verify-a2" />
          </div>
          <Button
            type="button"
            onClick={confirm}
            disabled={!amt1 || !amt2 || busy}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
            data-testid="bank-verify-confirm-btn"
          >
            {busy ? "Verifying…" : "Confirm amounts"}
          </Button>
        </>
      )}
    </div>
  );
}

